import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { updateProgress } from "../../../../../database";
import jumpAnimation from "../resources/jump_rope_animation.gif";
import jumpStill from "../resources/jump_rope_still.png";
import "../style/GameplayBox.css";

const difficulty_settings = {
    'easy': {time: 10, options: 3, speed: 1},
    'medium': {time: 7, options: 4, speed: 1.5},
    'hard': {time: 5, options: 5, speed: 2},
};


export default function GameplayBox({ unit, user, setPageState, gamestate, setGameState }) {
    const [currentOptions, setCurrentOptions] = useState([]);
    const [isMoving, setIsMoving] = useState(true);

    const settings = difficulty_settings[gamestate.difficulty];
    const [timeLeft, setTimeLeft] = useState(settings.time);

    const [feedback, setFeedback] = useState(null);
    const [isJumping, setIsJumping] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const [indicatorPosition, setIndicatorPosition] = useState(0);
    const animationFrameRef = useRef();
    const directionRef = useRef(1); // 1 for right, -1 for left

    
    const activeTargetIndex = isMoving
        ? Math.round(indicatorPosition)
        : null;

    // Generate options for current word
    useEffect(() => {
        const currentWord = gamestate.words[gamestate.currentWordIndex];
        const correctAnswer = currentWord.korean;
        const wrongOptions = gamestate.unit_words
            .filter((w) => w.korean !== correctAnswer)
            .sort(() => Math.random() - 0.5)
            .slice(0, settings.options - 1)
            .map((w) => w.korean);

        const allOptions = [correctAnswer, ...wrongOptions].sort(() => Math.random() - 0.5);
        setCurrentOptions(allOptions);
    }, [
        gamestate.currentWordIndex,
        gamestate.unit_words,
        gamestate.words,
        settings.options,
    ]);

    // Animate indicator
    useEffect(() => {
        if (!isMoving || currentOptions.length === 0) return;

        let position = indicatorPosition;
        const speed = 0.02 * settings.speed;

        const animate = () => {
            position += directionRef.current * speed;

            if (position >= currentOptions.length - 1) {
                position = currentOptions.length - 1;
                directionRef.current = -1;
            } else if (position <= 0) {
                position = 0;
                directionRef.current = 1;
            }

            setIndicatorPosition(position);
            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [
        isMoving,
        currentOptions.length,
        settings.speed,
        indicatorPosition,
    ]);
    
    // Timer countdown
    useEffect(() => {
        if (timeLeft <= 0 && isMoving) {
            handleTimeOut(); // Handle timeout specifically
            return;
        }

        if (!isMoving || feedback !== null) return;

        const timer = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 0.1));
        }, 100);

        return () => clearInterval(timer);
    }, [timeLeft, isMoving, feedback]);

    function checkGameOver(newState) {
        if (newState.lives <= 0 || newState.currentWordIndex >= gamestate.words.length) {
            setPageState("results");
        } else {
            setTimeLeft(settings.time);
            setIsMoving(true);
            setFeedback(null);
            setIsJumping(false);
            setSelectedIndex(null);
            setIndicatorPosition(0);
            directionRef.current = 1;
        }
    }


    const handleTimeOut = () => {
        const currentWord = gamestate.words[gamestate.currentWordIndex];
        setIsMoving(false);
        setSelectedIndex(null); // No selection made
        setFeedback("timeout");
        setIsJumping(false);

        if (user) {
            updateProgress(user.email, unit.unit, unit.textbook, "jumprope", false);
        }

        // Update game state
        setTimeout(() => {
            setGameState((prev) => {
                const newState = {
                    ...prev,
                    lives: prev.lives - 1, // Lose a life
                    wrongAnswers: [...prev.wrongAnswers, currentWord], // Track wrong answer
                    currentWordIndex: prev.currentWordIndex + 1,
                };

                checkGameOver(newState);

                return newState;
            });

        }, 2000);
    };


    const handleJump = () => {
        if (!isMoving) return;

        const currentWord = gamestate.words[gamestate.currentWordIndex];
        setIsMoving(false);
        const chosenIndex = activeTargetIndex;
        setSelectedIndex(chosenIndex);
        const chosenAnswer = currentOptions[chosenIndex];
        const isCorrect = chosenAnswer === currentWord.korean;

        setFeedback(isCorrect ? "correct" : "wrong");
        setIsJumping(isCorrect);

        if (user) {
            updateProgress(user.email, unit.unit, unit.textbook, "jumprope", isCorrect);
        }



        // Update game state
        setTimeout(() => {
            setGameState((prev) => {
                const newState = {
                    ...prev,
                    correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
                    lives: prev.lives - (isCorrect ? 0 : 1),
                    wrongAnswers: isCorrect ? prev.wrongAnswers : [...prev.wrongAnswers, currentWord],
                    currentWordIndex: prev.currentWordIndex + 1,
                };

                checkGameOver(newState);

                return newState;
            });
        }, 2000);
    };



     return (
        <div className="gameplay-container">
            {/* Game Area */}
            <div className="game-area">
                {/* Lives */}
                <div className="lives-left">
                    Lives: {gamestate.lives}
                </div>

                {/* Questions Progress */}
                <div className="questions-progress">
                    Questions: {gamestate.currentWordIndex} / {gamestate.words.length}
                </div>

                {/* Feedback message */}
                {feedback && (
                    <div className="feedback-message">
                        {feedback === "correct" && (
                        <div className="feedback-correct">✓ Correct!</div>
                        )}
                        
                        {(feedback === "wrong" || feedback === "timeout") && (
                        <div className="feedback-error">
                            {feedback === "timeout" ? "⏰ Time's up!" : "✗ Wrong!"}
                            {" "}The answer was: {gamestate.words[gamestate.currentWordIndex].korean}
                        </div>
                        )}
                    </div>
                )}

                {/* Timer */}
                <div className="time-left">
                    Time: {Math.ceil(timeLeft)}s
                </div>

                {/* English Word in Oval */}
                <div className="word-container">
                    <div className="word-oval">
                        <div className="word-text">
                            {gamestate.words[gamestate.currentWordIndex].englishLiterals.join(", ")}
                        </div>
                    </div>
                </div>

                {/* Character Options Container */}
                <div className="options-container">
                    <div className="options-grid" 
                        style={{ gridTemplateColumns: `repeat(${currentOptions.length}, 1fr)` }}
                    >
                        {currentOptions.map((option, index) => {
                            const isTargeted = activeTargetIndex === index;
                            let stateClass = "";
                            if (selectedIndex === index && feedback) {
                                stateClass = feedback === "correct" ? "correct" : "incorrect";
                            } else if (isTargeted && !feedback) {
                                stateClass = "targeted";
                            }

                            return (
                                <motion.div key={index} className="option-item">
                                <div className={`option-card ${stateClass}`}>
                                    <span className="option-text">{option}</span>
                                </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Moving Indicator */}
                <div className="indicator-track-container">
                    {/* Track Visualization */}
                    <div className="track-line" />
                    {/* Zone Dividers */}
                    {currentOptions.length > 1 &&
                        Array.from({ length: currentOptions.length - 1 }).map((_, i) => (
                            <div
                                key={i}
                                className="track-divider"
                                style={{
                                left: `${((i + 1) / currentOptions.length) * 100}%`,
                                }}
                            />
                        ))}

                    <AnimatePresence>
                        {isMoving && (
                        <motion.div
                            className="moving-indicator-arrow"
                            style={{
                            left: `${(indicatorPosition / Math.max(1, currentOptions.length - 1)) * 100}%`,
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            ↑
                        </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Jump Button */}
                <div className="jump-section">
                    {/* Character Animation */}
                    <div className="character-container">
                        <img
                            src={isJumping ? jumpAnimation : jumpStill}
                            alt="Game Character"
                            className="character-sprite"
                            key={isJumping ? "jump" : "idle"}
                        />
                    </div>

                    <button
                        onClick={handleJump}
                        disabled={!isMoving}
                        className="btn-jump"
                        style={{ fontFamily: "cursive" }}
                        >
                        Jump!
                    </button>
                </div>
            </div>

            <button
                className="btn btn-back game-options-back-button jump-rope-game-back-button"
                onClick={() => setPageState("setup")}
            >
                Back
            </button>
        </div>
    );
}
